import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import Loader from 'apollo-react/components/Loader';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Info from 'apollo-react-icons/Info';
import StatusDotSolid from 'apollo-react-icons/StatusDotSolid';
import moment from 'moment';
import qs from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import ActionButtonDropdown from './components/ActionButtonDropdown';
import SaveAsModal from './components/SaveAsModal';
import UserChangesButton from './components/UserChangesButton';
import { exportForm, setOpenFormHistoryModal } from '../../../actions/FormHistoryActions';
import {
  downloadAllFormsRules,
  downloadDeltaChanges,
  downloadFieldsToFormMapping,
  downloadFormRules,
  downloadMergedDeltaChanges,
} from '../../../api/forms';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { FORMS_LIST, HOME } from '../../../constants/Urls';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { changeURLParam, getUITA } from '../../../utils/commonUtils';
import { DATE_FORMAT_WITH_TIMESTAMP } from '../../../utils/dateUtils';
import { removeSearchParam } from '../../../utils/locationUtils';
import { getDomainUrl } from '../../../utils/qs';
import { isEditorCompactMode } from '../../../utils/windowDimensionsUtils';
import {
  cancelEditor,
  deleteForm,
  publishFormEditor,
  saveEditor,
  unpublishFormEditor,
} from '../../actions/editor.actions';
import {
  FORM_RELEASE_TYPE_CUSTOM,
  FORM_RELEASE_TYPE_PILGRIM,
  FORM_STATUS_INWORKS,
  FORMS_DESIGN_RIGHT,
  SAVE_ACTION_TYPE,
  STD_PILGRIM_RIGHT,
  URL_QUERY_PARAMS,
} from '../../constants/editor.constants';
import { getFormInfo, getUserRights } from '../../selectors/common.selectors';
import { getIsImportMode, getSaveDisabled } from '../../selectors/editor.selectors';
import { getStatusText } from '../../utils/editor.utils';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px 20px 32px',
    '& ul': {
      marginLeft: '15px',
    },
  },
  formInfoText: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  formInfoTextContainer: {
    marginRight: 55,
    margin: 5,
    display: 'flex',
    alignItems: 'start',
  },
  formInfoTextTitleContainer: {
    marginRight: 10,
  },

  formInfoToolTip: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  formInfoTextTitle: {
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    color: 'var(--black)',
    height: 21,
  },
  dotLegend: {
    display: 'flex',
    height: 24,
    marginRight: 10,
  },
  uiChanges: {
    color: 'orange',
  },
  deltaChanges: {
    color: 'blue',
  },
  addedChanges: {
    color: 'green',
  },
  titleWrapper: {
    '@media (max-width: 1200px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
      paddingRight: 28,
    },
  },
  hiddenMenuButton: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  marginRight: {
    marginRight: 15,
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  resetOverflow: {
    overflow: 'visible!important',
    minWidth: 'initial!important',
  },
});

const EditorHeader = () => {
  const classes = useStyles();
  const history = useHistory();
  const formInfo = useFormSelector(getFormInfo);
  const location = useLocation();
  const { entityType, isCreateForm, isImport, formId: linkFormId, standaloneLayout } = qs.parse(
    location.search
  );
  const translateString = useTranslation();
  const userRights = useSelector(getUserRights);
  const isImportMode = useSelector(getIsImportMode);

  const isSaveDisabled = useFormSelector(getSaveDisabled);
  const isPublishDisabled =
    formInfo.formReleaseType === FORM_RELEASE_TYPE_CUSTOM
      ? isCreateForm && formInfo.formState !== FORM_STATUS_INWORKS
      : formInfo.formState !== FORM_STATUS_INWORKS;

  const publishButtonDisplayedText =
    formInfo.formState === 1 && formInfo.formReleaseType === FORM_RELEASE_TYPE_CUSTOM
      ? 'Unpublish'
      : 'Publish';

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [isSaveAsModalOpen, setSaveAsModalOpen] = useState(false);

  const hasFormsDesignRight = useMemo(() => userRights.includes(FORMS_DESIGN_RIGHT), [userRights]);
  const hasStdPilgrimRight = useMemo(() => userRights.includes(STD_PILGRIM_RIGHT), [userRights]);
  const formName = translateString(formInfo.formKey);

  useEffect(() => {
    document.title = (formName && `Forms Editor / ${formName}`) || 'Initiate';
  }, [formName]);

  const redirectToPrevPage = () => {
    if (standaloneLayout) {
      history.push(`${FORMS_LIST}`);
    } else if (history.length > 1) {
      history.goBack();
    } else {
      history.push(`${HOME}`);
    }
  };

  const redirectToEntityDesigner = () => {
    const entityName = formInfo.formEntityKey;

    window.open(
      `${getDomainUrl()}/entity-details?entityName=${entityName}&standaloneLayout=true&isEditMode=false`
    );
  };

  const redirectToAnotherForm = ({ formId }) => {
    if (linkFormId) {
      changeURLParam({ paramName: 'formId', paramValue: formId });
    }
  };

  const handlePublishButton = async () => {
    setIsLoading(true);
    if (formInfo.formState === 1 && formInfo.formReleaseType === FORM_RELEASE_TYPE_CUSTOM) {
      await dispatch(unpublishFormEditor(formInfo.formKey));
      const cleanedURL = removeSearchParam(window.location.href, URL_QUERY_PARAMS.FORM_ID); //URL without formId search param
      setIsLoading(false);
      window.location.replace(cleanedURL);
    } else {
      await dispatch(publishFormEditor({ redirectToPrevPage }));
      setIsLoading(false);
    }
  };

  const handleSaveButton = async ({ saveAction } = {}) => {
    setIsLoading(true);
    await dispatch(saveEditor({ saveAction, redirectToAnotherForm }));
    setIsLoading(false);
  };

  const handleDeleteForm = async () => {
    setIsDeleteModalOpened(false);
    setIsLoading(true);
    await dispatch(
      deleteForm({ formId: formInfo.formId, formKey: formInfo.formKey }, redirectToPrevPage)
    );
    setIsLoading(false);
  };

  const downloadFieldsFormMapping = async () => {
    setIsLoading(true);

    await downloadFieldsToFormMapping(isImport ? formInfo?.formEntityKey : entityType, dispatch);
    setIsLoading(false);
  };

  const displayFormHistoryModal = () => {
    dispatch(setOpenFormHistoryModal(true));
  };

  const downloadDelta = async () => {
    const requestBody = {
      formKey: formInfo.formKey,
      formReleaseType: formInfo.formReleaseType,
      formState: getStatusText(formInfo.formState),
    };
    await downloadDeltaChanges(requestBody);
  };

  const handleExportForm = () => {
    dispatch(exportForm({ formId: formInfo.formId, fileName: formInfo.formKey }));
  };

  const handleDownloadMergedChanges = async () => {
    await downloadMergedDeltaChanges({
      formKey: formInfo.formKey,
      fromImport: false,
    });
  };

  const handleDownloadFormRules = async () => {
    const requestBody = {
      formKey: formInfo.formKey,
      formReleaseType: formInfo.formReleaseType,
      formState: getStatusText(formInfo.formState),
    };
    await downloadFormRules(requestBody);
  };

  const handleDownloadAllFormsRules = async () => {
    await downloadAllFormsRules();
  };

  const handleCancelButton = () => {
    dispatch(cancelEditor());
    redirectToPrevPage();
  };

  const actionItems = [
    {
      text: translateString('Save'),
      onClick: () =>
        handleSaveButton({
          saveAction: isImportMode
            ? SAVE_ACTION_TYPE.IMPORT
            : isCreateForm && SAVE_ACTION_TYPE.CREATE_FORM,
        }),
      disabled: !isImportMode && isSaveDisabled,
      hidden: !isEditorCompactMode(),
    },
    {
      text: translateString('Save As'),
      onClick: () => setSaveAsModalOpen(true),
      hidden: !hasFormsDesignRight,
    },
    {
      text: translateString('Save As Pilgrim'),
      onClick: () =>
        handleSaveButton({
          saveAction: isImportMode
            ? SAVE_ACTION_TYPE.IMPORT_AS_PILGRIM
            : SAVE_ACTION_TYPE.SAVE_AS_PILGRIM,
        }),
      hidden: !hasStdPilgrimRight || !hasFormsDesignRight,
    },
    {
      text: translateString('Cancel'),
      onClick: handleCancelButton,
      hidden: !isEditorCompactMode(),
    },
    { isDivider: true },
    {
      text: translateString(publishButtonDisplayedText),
      onClick: handlePublishButton,
      disabled: isPublishDisabled,
      hidden: !isEditorCompactMode(),
    },
    {
      text: translateString('View History'),
      onClick: displayFormHistoryModal,
    },
    {
      text: translateString('Delete'),
      onClick: () => setIsDeleteModalOpened(true),
      hidden:
        formInfo.formReleaseType === FORM_RELEASE_TYPE_PILGRIM ||
        formInfo.formState !== FORM_STATUS_INWORKS ||
        isImportMode,
    },
    { isDivider: true },
    {
      text: translateString('Export'),
      onClick: handleExportForm,
      hidden: isImportMode,
    },
    { isDivider: true },
    {
      text: translateString('Entity Designer'),
      onClick: redirectToEntityDesigner,
      hidden: !hasFormsDesignRight,
    },
    {
      text: translateString('Fields To Forms Mapping'),
      onClick: downloadFieldsFormMapping,
    },
    { isDivider: true },
    {
      text: translateString('Download All Forms Rules Document'),
      onClick: handleDownloadAllFormsRules,
      hidden: !hasFormsDesignRight,
    },
    {
      text: translateString('Download Form Rules Document'),
      onClick: handleDownloadFormRules,
    },
    { isDivider: true },
    {
      text: translateString('Download Delta Changes Document'),
      onClick: downloadDelta,
    },
    {
      text: translateString('Download Merged Delta Changes Document'),
      onClick: handleDownloadMergedChanges,
      hidden: formInfo.formReleaseType === FORM_RELEASE_TYPE_PILGRIM,
    },
  ];

  const userChangesInfoList = [
    {
      text: 'Controls in Blue have published configurations (property changes)',
      className: 'deltaChanges',
    },
    {
      text: 'Controls in Green are published custom fields added in the form',
      className: 'addedChanges',
    },
    {
      text: 'Controls in Orange have configurations that are in progress',
      className: 'uiChanges',
    },
  ];

  const formLastModifiedDate = formInfo.formLastModified
    ? moment(formInfo.formLastModified).format(DATE_FORMAT_WITH_TIMESTAMP)
    : ' ';

  const formStatus =
    formInfo.formState === 3
      ? 'None'
      : formInfo.formState === 2
      ? 'Unpublished'
      : formInfo.formState === 1
      ? 'Published'
      : 'Inworks';

  const formInfoArr = [
    { title: `Name: ${formInfo.formKey}`, subtitle: '' },
    { title: `Last Modified Date: ${formLastModifiedDate}`, subtitle: '' },
    {
      title: `Status: ${formStatus}`,
      subtitle: '',
    },
    { title: `Version Number: ${formInfo.formVersion}`, subtitle: '' },
    { title: `Release Version: ${formInfo.formReleaseVersion}`, subtitle: '' },
    { title: `Release Type: ${formInfo.formReleaseType}`, subtitle: '' },
  ];

  const deleteConfirmMessage = (
    <>
      {translateString('Do you really want to delete form')} <b>{formInfo.formKey}</b>
      {'?'}
    </>
  );

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid className={classes.titleWrapper} item xs={8}>
          <span className={classes.formInfoToolTip}>
            {formInfo && (
              <Tooltip extraLabels={formInfoArr} placement="right" variant="light">
                <IconButton aria-label={translateString('Info')}>
                  <Info color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </span>
          <span className={classes.formInfoText} ui-ta={getUITA('FormInfo')}>
            <div className={classes.formInfoTextContainer}>
              <div className={classes.formInfoTextTitleContainer}>
                <Typography className={classes.formInfoTextTitle}>{'Name:'}</Typography>
                <Typography className={classes.formInfoTextTitle}>{'Version:'}</Typography>
              </div>
              <div>
                <Typography className={classes.formInfoTextTitle}>{formInfo.formKey}</Typography>
                <Typography className={classes.formInfoTextTitle}>
                  {formInfo.formVersion}
                </Typography>
              </div>
            </div>
            <div className={classes.formInfoTextContainer}>
              <div className={classes.formInfoTextTitleContainer}>
                <Typography className={classes.formInfoTextTitle}>
                  {'Last Modified Date:'}
                </Typography>
                <Typography className={classes.formInfoTextTitle}>{'Release Type:'}</Typography>
              </div>
              <div>
                <Typography className={classes.formInfoTextTitle}>
                  {formLastModifiedDate}
                </Typography>
                <Typography className={classes.formInfoTextTitle}>
                  {formInfo.formReleaseType}
                </Typography>
              </div>
            </div>
            <div className={classes.formInfoTextContainer}>
              <div className={classes.formInfoTextTitleContainer}>
                <Typography className={classes.formInfoTextTitle}>{'Status:'}</Typography>
                <Typography className={classes.formInfoTextTitle}>{'Release Version:'}</Typography>
              </div>
              <div>
                <Typography className={classes.formInfoTextTitle}>{formStatus}</Typography>
                <Typography className={classes.formInfoTextTitle}>
                  {formInfo.formReleaseVersion}
                </Typography>
              </div>
            </div>
          </span>
        </Grid>
        <Grid className={classes.buttonsWrapper} item xs={4}>
          <>
            <UserChangesButton />
            <div className={classes.dotLegend}>
              {userChangesInfoList.map(({ text, className }) => (
                <div className={classes[className]} key={className}>
                  <Tooltip
                    extraLabels={[
                      {
                        title: (
                          <Typography className={classes[className]}>
                            {translateString(text)}
                          </Typography>
                        ),
                        subtitle: '',
                      },
                    ]}
                    placement="bottom"
                    variant="light"
                  >
                    <StatusDotSolid />
                  </Tooltip>
                </div>
              ))}
            </div>
            <ActionButtonDropdown items={actionItems} />
            <Button
              variant="text"
              className={`${classes.marginRight} ${classes.resetOverflow}`}
              onClick={handleCancelButton}
              ui-ta={getUITA('Editor Header Button', 'Cancel')}
              aria-label={translateString('Cancel')}
            >
              {translateString('Cancel')}
            </Button>
            <Button
              variant="secondary"
              className={`${classes.marginRight} ${classes.resetOverflow}`}
              onClick={() =>
                handleSaveButton({
                  saveAction: isImportMode
                    ? SAVE_ACTION_TYPE.IMPORT
                    : isCreateForm && SAVE_ACTION_TYPE.CREATE_FORM,
                })
              }
              disabled={!isImportMode && isSaveDisabled}
              ui-ta={getUITA('Editor Header Button', 'Save')}
              aria-label={translateString('Save')}
            >
              {translateString('Save')}
            </Button>
            <Button
              variant="primary"
              className={`${classes.marginRight} ${classes.resetOverflow}`}
              onClick={handlePublishButton}
              disabled={isPublishDisabled || isImportMode}
              ui-ta={getUITA('Editor Header Button', publishButtonDisplayedText)}
              aria-label={translateString(publishButtonDisplayedText)}
            >
              {translateString(publishButtonDisplayedText)}
            </Button>
            {isLoading && <Loader isInner />}
          </>
        </Grid>
      </Grid>
      {isSaveAsModalOpen && (
        <SaveAsModal
          isOpen={isSaveAsModalOpen}
          initialFormName={formInfo.formKey}
          handleClose={() => setSaveAsModalOpen(false)}
          setIsLoading={setIsLoading}
          redirectToAnotherForm={redirectToAnotherForm}
        />
      )}
      <DeleteConfirmationModal
        message={deleteConfirmMessage}
        open={isDeleteModalOpened}
        onClose={() => setIsDeleteModalOpened(false)}
        onDeleteConfirm={handleDeleteForm}
      />
    </div>
  );
};

export default EditorHeader;
